import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, ArrowLeft } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const gold = "rounded-none h-12 px-8 bg-[#b8924a] hover:bg-[#a07f3e] text-white uppercase tracking-[0.2em] text-[11px] font-medium border border-[#b8924a]";
const outline = "rounded-none h-12 px-8 border border-white/40 text-white bg-transparent hover:bg-white hover:text-[#0c1729] uppercase tracking-[0.2em] text-[11px] font-medium";

export function PayStepQuestion({ onYes, onNo }) {
  return (
    <div data-testid="pay-step-question">
      <h1 className="font-serif text-white text-3xl md:text-5xl font-light leading-tight">
        Are you intending to make a <span className="italic">retainer deposit</span> into the trust account?
      </h1>
      <div className="mt-12 flex flex-col sm:flex-row sm:items-end gap-6">
        <Button onClick={onYes} className={`${gold} w-full sm:w-80`} data-testid="pay-yes-button">Yes <ArrowRight className="w-4 h-4 ml-3" /></Button>
        <div className="flex flex-col gap-2 w-full sm:w-80">
          <p className="text-sm text-stone-300">If you are intending to pay an invoice, press No.</p>
          <Button variant="outline" onClick={onNo} className={`${outline} w-full`} data-testid="pay-no-button">No</Button>
        </div>
      </div>
    </div>
  );
}

export function PayStepAmount({ onNext, onBack }) {
  const [value, setValue] = useState("");
  const amount = parseFloat(value.replace(/[^0-9.]/g, ""));
  const valid = !isNaN(amount) && amount > 0;
  return (
    <form data-testid="pay-step-amount" onSubmit={(e) => { e.preventDefault(); if (valid) onNext(amount); }}>
      <h1 className="font-serif text-white text-3xl md:text-5xl font-light leading-tight">Thank you for making a retainer deposit.</h1>
      <p className="mt-6 text-lg text-stone-300 font-light">Please enter the amount of the deposit.</p>
      <label className="block mt-10 text-[11px] uppercase tracking-[0.3em] text-[#d4b87a]" htmlFor="deposit-amount">Deposit amount (USD)</label>
      <div className="mt-2 flex items-center max-w-sm border border-white/30 bg-white/5 focus-within:border-[#d4b87a] transition-colors">
        <span className="pl-4 text-2xl font-serif text-[#d4b87a]">$</span>
        <Input
          id="deposit-amount"
          inputMode="decimal"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="0.00"
          className="border-0 bg-transparent h-14 text-2xl font-serif text-white placeholder:text-stone-500 focus-visible:ring-0 rounded-none"
          data-testid="pay-amount-input"
        />
      </div>
      <div className="mt-10 flex flex-wrap gap-4">
        <Button type="submit" disabled={!valid} className={gold} data-testid="pay-next-button">Next <ArrowRight className="w-4 h-4 ml-3" /></Button>
        <Button type="button" variant="outline" onClick={onBack} className={outline} data-testid="pay-back-button"><ArrowLeft className="w-4 h-4 mr-3" /> Back</Button>
      </div>
    </form>
  );
}

const DEFAULT_CONTACT_MESSAGE = "Based on the amount of your retainer deposit, please contact HBHS for next steps.";

export function PayStepContact({ firm, message, testId = "pay-step-contact" }) {
  const heading = message || firm.trustDepositContactMessage || DEFAULT_CONTACT_MESSAGE;
  return (
    <div data-testid={testId}>
      <h1 className="font-serif text-white text-3xl md:text-5xl font-light leading-tight">{heading}</h1>
      <div className="mt-12 space-y-4 text-stone-200">
        {(firm.offices || []).map((office, i) => {
          const phone = firm.phones?.[i];
          if (!phone) return null;
          return (
            <a key={office.city} href={`tel:${phone.replace(/\D/g, "")}`} className="flex items-center gap-3 hover:text-white transition" data-testid={`pay-contact-phone-${office.city.toLowerCase()}`}>
              <Phone className="w-4 h-4 text-[#d4b87a]" /> <span className="font-serif text-xl">{phone}</span><span className="text-stone-400 text-sm">{office.label}</span>
            </a>
          );
        })}
        <a href={`mailto:${firm.email}`} className="flex items-center gap-3 hover:text-white transition" data-testid="pay-contact-email">
          <Mail className="w-4 h-4 text-[#d4b87a]" /> <span className="font-serif text-xl">{firm.email}</span>
        </a>
      </div>
      <Button asChild variant="outline" className={`${outline} mt-12`}>
        <Link to="/" data-testid="pay-return-home"><ArrowLeft className="w-4 h-4 mr-3" /> Return Home</Link>
      </Button>
    </div>
  );
}

export function PayStepRedirect() {
  return (
    <div data-testid="pay-step-redirect" className="text-stone-300 font-light text-lg">Taking you to the secure payment page&hellip;</div>
  );
}
